import { createPortal } from 'react-dom';
import { useState } from 'react';
import { X, Bell, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const WaitlistModal = ({ onClose, forced = false, source = 'pricing_page', planId = null }) => {
  const { user } = useAuth();
  const [contact, setContact] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const planLabel = planId
    ? planId.charAt(0).toUpperCase() + planId.slice(1)
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/waitlist', {
        contact: contact.trim(),
        source,
        planInterest: planId,
      });
      setDone(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={forced ? undefined : onClose}
    >
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" />
      <div
        className="relative bg-white dark:bg-stone-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-stone-100 dark:border-stone-800">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center shrink-0 mt-0.5">
                <Bell className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-display text-stone-900 dark:text-stone-100">
                  {forced ? "You've used your free minutes" : 'Be First to Know'}
                </h2>
                <p className="text-sm text-stone-500 dark:text-stone-400 font-jakarta mt-1 leading-snug">
                  {forced
                    ? 'Paid plans are coming soon. Join the waitlist and get early access.'
                    : `Paid plans are launching soon${planLabel ? `, including ${planLabel}` : ''}. Be first in line.`}
                </p>
              </div>
            </div>
            {!forced && (
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-stone-400 hover:text-stone-600 hover:bg-stone-100 dark:text-stone-500 dark:hover:text-stone-300 dark:hover:bg-stone-800 transition-colors -mt-1 -mr-1 shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="p-8">
          {done ? (
            <div className="text-center py-2">
              <div className="w-14 h-14 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-emerald-500 dark:text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold font-display text-stone-900 dark:text-stone-100 mb-2">You&apos;re on the list!</h3>
              <p className="text-stone-500 dark:text-stone-400 font-jakarta text-sm mb-6 leading-relaxed">
                We&apos;ll email you as soon as paid plans go live. Waitlist members get early access pricing.
              </p>
              <button
                onClick={onClose}
                className="w-full py-3 bg-stone-100 hover:bg-stone-200 text-stone-900 dark:bg-stone-800 dark:hover:bg-stone-700 dark:text-stone-100 rounded-xl font-jetbrains text-sm font-bold transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-bold font-jetbrains text-stone-600 dark:text-stone-300 mb-1.5 uppercase tracking-wider">
                  Email address
                </label>
                <input
                  type="email"
                  value={contact}
                  onChange={(e) => { setContact(e.target.value); setError(''); }}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800/50 text-stone-900 dark:text-stone-100 font-jakarta text-sm placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-300 dark:focus:border-indigo-700 transition-all"
                  autoFocus
                />
                {error && <p className="text-xs text-red-500 dark:text-red-400 font-jakarta mt-1.5">{error}</p>}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-stone-900 hover:bg-stone-800 text-white dark:bg-stone-100 dark:hover:bg-white dark:text-stone-900 rounded-xl font-jetbrains text-sm font-bold transition-colors disabled:opacity-60"
              >
                {submitting ? 'Joining...' : 'Join Waitlist →'}
              </button>

              <p className="text-center text-xs text-stone-400 dark:text-stone-500 font-jakarta">
                No spam. One email when plans launch.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WaitlistModal;
